import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	VersionsService,
	type IGetVersionsParams,
} from '@services/versions.service';

// --- Stores ---
import { VersionsStore } from '@stores/versions.store';

// --- Interfaces ---
import type { IVersion } from '@interfaces/version';

const REFRESH_INTERVAL = 120 * 10000;

@Injectable({
	providedIn: 'root',
})
export class VersionsFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);

	private readonly _filter = new BehaviorSubject<IGetVersionsParams>({});
	readonly filter$ = this._filter.asObservable().pipe(distinctUntilChanged());

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getVersions({ ...this._filter.value })));

	readonly versionsState$ = combineLatest([
		this.versionsStore.versionsState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private versionsService: VersionsService,
		private versionsStore: VersionsStore
	) {}

	getVersions(queryParams?: IGetVersionsParams) {
		return this.versionsService
			.getVersions(queryParams)
			.pipe(tap(versions => this.versionsStore.updateVersions(versions)));
	}

	getVersionById(id: IVersion['id']) {
		return this.versionsService.getVersion(id);
	}

	getLatestVersion(queryParams?: IGetVersionsParams) {
		return this.versionsService.getLatestVersion(queryParams);
	}

	newVersion(version: IVersion) {
		return this.versionsService
			.newVersion(version)
			.pipe(tap(version => this.versionsStore.updateVersion(version)));
	}

	activateVersion(version: IVersion) {
		return this.versionsService
			.activate(version.id)
			.pipe(tap(() => this.versionsStore.deleteVersion(version)));
	}

	updateVersion(id: IVersion['id'], version: IVersion) {
		return this.versionsService
			.editVersion(id, version)
			.pipe(tap(() => this.versionsStore.updateVersion(version)));
	}

	deleteVersion(version: IVersion) {
		return this.versionsService
			.deleteVersion(version.id)
			.pipe(tap(() => this.versionsStore.deleteVersion(version)));
	}

	filterVersions(filter: IGetVersionsParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}
}
